import { ContentType, Recnum, UUID } from '../../types/common';
import { DTO } from '../../utilities/Dto';
import { MakeOptionalExcept } from '../../utilities/MakeOptionalExcept';
import { MakeRequired } from '../../utilities/MakeRequired';
import { assertNever } from '../../utilities/assertNever';
import { v4 as uuidV4 } from 'uuid';

export type Position = {
  coords: {
    speed: number | null;
    heading: number | null;
    accuracy: number | null;
    altitude: number | null;
    altitudeAccuracy: number | null;
    latitude: number;
    longitude: number;
  };
  timestamp: number;
};

export type TicketEventType =
  | 'ADD_ATTACHMENT'
  | 'ADD_LOCATION'
  | 'ADD_MESSAGE'
  | 'CREATE_TICKET'
  | 'DELETE_ATTACHMENT'
  | 'DELETE_MESSAGE'
  | 'DELETE_LOCATION'
  | 'NOTIFICATION_OPT_IN'
  | 'NOTIFICATION_OPT_OUT'
  | 'UPDATE_LOCATION'
  | 'UPDATE_MESSAGE'
  | 'UPDATE_TICKET';

export type AddAttachmentPayload = {
  checksum: string;
  size: number;
  contentType: ContentType;
  filename?: string;
};

export type EditableTicketFields = Partial<
  Pick<
    Ticket,
    | 'title'
    | 'description'
    | 'status'
    | 'due'
    | 'assignee_user_id'
    | 'executor_user_id'
  >
> & {
  /**
   * CRM chain as a WRITER may state it — narrower than the columns on Ticket,
   * which stay nullable for legacy rows. See zEditibleTicketFields.
   */
  project_id?: Recnum;
  werk_id?: Recnum | null;
  client_id?: Recnum;
};

export type AddLocationPayload = { label: string; position: Position };
export type AddMessagePayload = { message: string };
export type CreateTicketPayload = MakeRequired<
  EditableTicketFields & { add_labels?: number[] },
  'title'
>;
export type DeleteAttachmentPayload = { eventId: UUID };
export type DeleteMessagePayload = { eventId: UUID };
export type DeleteLocationPayload = { eventId: UUID };
export type NotificationOptInPayload = {};
export type NotificationOptOutPayload = {};
export type UpdateLocationPayload = { eventId: UUID; label: string };
export type UpdateMessagePayload = { eventId: UUID; message: string };
export type UpdateTicketPayload = Partial<
  EditableTicketFields & {
    add_labels?: number[];
    remove_labels?: number[];
  }
>;

export type MakeTicketEvent<Type extends TicketEventType, Payload> = {
  type: Type;
  payload: Payload;
} & BaseTicketEvent;

export type AddAttachmentTicketEvent = MakeTicketEvent<
  'ADD_ATTACHMENT',
  AddAttachmentPayload
>;
export type AddLocationTicketEvent = MakeTicketEvent<
  'ADD_LOCATION',
  AddLocationPayload
>;
export type AddMessageTicketEvent = MakeTicketEvent<
  'ADD_MESSAGE',
  AddMessagePayload
>;
export type CreateTicketEvent = MakeTicketEvent<
  'CREATE_TICKET',
  CreateTicketPayload
>;
export type DeleteAttachmentTicketEvent = MakeTicketEvent<
  'DELETE_ATTACHMENT',
  DeleteAttachmentPayload
>;
export type DeleteMessageTicketEvent = MakeTicketEvent<
  'DELETE_MESSAGE',
  DeleteMessagePayload
>;
export type DeleteLocationTicketEvent = MakeTicketEvent<
  'DELETE_LOCATION',
  DeleteLocationPayload
>;
export type NotificationOptInTicketEvent = MakeTicketEvent<
  'NOTIFICATION_OPT_IN',
  NotificationOptInPayload
>;
export type NotificationOptOutTicketEvent = MakeTicketEvent<
  'NOTIFICATION_OPT_OUT',
  NotificationOptOutPayload
>;
export type UpdateLocationTicketEvent = MakeTicketEvent<
  'UPDATE_LOCATION',
  UpdateLocationPayload
>;
export type UpdateMessageTicketEvent = MakeTicketEvent<
  'UPDATE_MESSAGE',
  UpdateMessagePayload
>;
export type UpdateTicketEvent = MakeTicketEvent<
  'UPDATE_TICKET',
  UpdateTicketPayload
>;

export type TicketEvent =
  | AddAttachmentTicketEvent
  | UpdateTicketEvent
  | AddLocationTicketEvent
  | AddMessageTicketEvent
  | CreateTicketEvent
  | DeleteAttachmentTicketEvent
  | DeleteMessageTicketEvent
  | DeleteLocationTicketEvent
  | NotificationOptInTicketEvent
  | NotificationOptOutTicketEvent
  | UpdateLocationTicketEvent
  | UpdateMessageTicketEvent;

export interface EventMeta {
  originTicketId?: UUID;
  referencedTicketIds?: UUID[];
  referencedUserIds?: Recnum[];
  bulkOperationId?: UUID;
}

export interface BaseTicketEvent {
  id: UUID;
  recnum?: Recnum | null;

  type: string;
  payload: unknown;
  meta?: EventMeta;

  ticket_id: UUID;
  user_id: Recnum;

  created: Date;
  inserted?: Date | null;
}

export type Draft<TE extends TicketEvent> = Omit<TE, 'recnum' | 'inserted'>;

export type TicketStatus = 'opened' | 'closed';

export interface Ticket {
  id: UUID;
  recnum: Recnum | null;

  number: number | null;
  title: string;
  description: string;
  status: TicketStatus;
  due: Date | null;
  labels: Recnum[];
  close_date: Date | null;

  project_id: Recnum | null;
  werk_id: Recnum | null;
  /**
   * Denormalized klant of the project/werk, maintained through the event
   * stream: payloads that set project_id or werk_id also carry client_id.
   * Enables plain, offline-emulatable `client_id=eq.N` queries.
   */
  client_id: Recnum | null;
  assignee_user_id: Recnum | null;
  creator_user_id: Recnum;
  executor_user_id: Recnum | null;

  created: Date;
  modified: Date;
  notification_opt_in: Recnum[];
  notification_opt_out: Recnum[];
  ticket_references: UUID[];
  deleted: boolean;
}

export interface TicketWithEvents extends Ticket {
  events: TicketEvent[];
}

/**
 * Safely parses an arbitrary value to a Ticket status.
 * Returns undefined if the value is not a valid status.
 */
export function parseTicketStatus(value: unknown): TicketStatus | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.toLowerCase();
  if (normalized === 'opened' || normalized === 'closed') {
    return normalized as Ticket['status'];
  }
  return undefined;
}

export function parseTicketDto(ticket: DTO<Ticket>): Ticket {
  return {
    ...ticket,
    due: ticket.due ? new Date(ticket.due) : null,
    close_date: ticket.close_date ? new Date(ticket.close_date) : null,
    created: new Date(ticket.created),
    modified: new Date(ticket.modified),
  };
}
export function parseTicketWithEventsDto(
  ticket: DTO<TicketWithEvents>,
): TicketWithEvents {
  return {
    ...ticket,
    due: ticket.due ? new Date(ticket.due) : null,
    close_date: ticket.close_date ? new Date(ticket.close_date) : null,
    created: new Date(ticket.created),
    modified: new Date(ticket.modified),
    events: ticket.events.map(parseTicketEventDto),
  };
}
export function parseTicketEventDto(event: DTO<TicketEvent>): TicketEvent {
  switch (event.type) {
    case 'UPDATE_TICKET':
      return {
        ...event,
        created: new Date(event.created),
        inserted:
          typeof event.inserted === 'string'
            ? new Date(event.inserted)
            : event.inserted,
        payload: {
          ...event.payload,
          due:
            typeof event.payload.due === 'string'
              ? new Date(event.payload.due)
              : event.payload.due,
        },
      };
    case 'CREATE_TICKET':
      return {
        ...event,
        created: new Date(event.created),
        inserted:
          typeof event.inserted === 'string'
            ? new Date(event.inserted)
            : event.inserted,
        payload: {
          ...event.payload,
          due:
            typeof event.payload.due === 'string'
              ? new Date(event.payload.due)
              : event.payload.due,
        },
      };
    case 'ADD_ATTACHMENT':
    case 'ADD_LOCATION':
    case 'ADD_MESSAGE':
    case 'DELETE_ATTACHMENT':
    case 'DELETE_LOCATION':
    case 'DELETE_MESSAGE':
    case 'NOTIFICATION_OPT_IN':
    case 'NOTIFICATION_OPT_OUT':
    case 'UPDATE_LOCATION':
    case 'UPDATE_MESSAGE':
      return {
        ...event,
        created: new Date(event.created),
        inserted:
          typeof event.inserted === 'string'
            ? new Date(event.inserted)
            : event.inserted,
      };
    default:
      assertNever(event);
  }
}

export function createTicketEvent<
  T extends TicketEvent['type'],
  E extends TicketEvent = Extract<TicketEvent, { type: T }>,
>(
  type: T,
  partial: MakeOptionalExcept<E, 'ticket_id' | 'payload' | 'user_id'>,
): E {
  return {
    created: new Date(),
    id: uuidV4(),
    inserted: null,
    recnum: null,
    type,
    ...partial,
  } as any as E;
}
